import { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, Card, Tab } from "react-bootstrap";

import ShipmentRackIcon from "../../../../components/icons/ShipmentRackIcon";

import ServerSideTable from "../../../../components/tables/ServerSideTable";

import racksTableColumns from "./racksTableColumns";

import shipmentService from "../../../../services/shipmentService";

const ShipmentRacks = ({
  selectedListingAccount,
  setSelectedShipmentRack,
  shipmentCategories,
  submitting,
  shipmentRacks,
  setShipmentRacks,
}) => {
  const [activeShipmentCategory, setActiveShipmentCategory] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const [paginationOptions, setPaginationOptions] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });
  const [totalCount, setTotalCount] = useState(0);

  // Fetch data when the activeShipmentCategory changes
  useEffect(() => {
    if (!activeShipmentCategory) return;

    let isMounted = true;

    const fetchDataSafe = async () => {
      await fetchData({
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: "lastUpdated", desc: true }],
      });

      if (!isMounted) return;
    };

    fetchDataSafe();

    setSelectedShipmentRack();

    return () => {
      isMounted = false;
    };
  }, [activeShipmentCategory]);

  // Reset activeTab when account is selected.
  useEffect(() => {
    setActiveShipmentCategory();
    setSelectedShipmentRack();
  }, [selectedListingAccount]);

  const handleRowClick = (row) => {
    setSelectedShipmentRack(row.original);
    window.scrollTo(0, 0);
  };

  const fetchData = useCallback(
    async (params) => {
      setIsLoading(true);

      const { globalFilter, filters, pageIndex, pageSize, sortBy } = params;

      // Change any date parameters into date range parameters.
      const queryParams = filters?.reduce((acc, curr) => {
        if (!acc[curr.id] && curr.id !== "lastUpdated") {
          acc[curr.id] = curr.value;
        } else if (curr.id === "lastUpdated") {
          acc["lastUpdatedStart"] = curr.value[0];
          acc["lastUpdatedEnd"] = curr.value[1];
        }

        return acc;
      }, {});

      const result = await shipmentService.getShipmentRacks({
        listingAccountId: selectedListingAccount.listingAccountId,
        shipmentCategoryId: activeShipmentCategory,
        globalSearch: globalFilter,
        ...queryParams,
        pageIndex,
        pageSize,
        sortBy,
      });

      setShipmentRacks(result.shipmentRacks);
      setPaginationOptions({
        pageIndex: result.pageIndex,
        pageSize: result.pageSize,
        pageCount: result.pageCount,
      });
      setTotalCount(result.totalCount);

      setIsLoading(false);
    },
    [activeShipmentCategory, selectedListingAccount]
  );

  return (
    <Card>
      <Tab.Container activeKey={activeShipmentCategory}>
        <Card.Header>
          <div className="d-flex justify-content-start mb-3">
            <ShipmentRackIcon size="50" className="me-3" />
            <div>
              <Card.Title>Shipment Racks</Card.Title>
              <Card.Subtitle>
                Select a shipment category below to view{" "}
                <strong>{selectedListingAccount.listingAccount}</strong>'s
                shipment racks.
              </Card.Subtitle>
            </div>
          </div>
          <ButtonGroup className="d-flex">
            {shipmentCategories &&
              shipmentCategories
                .filter(
                  (category) =>
                    category.shipmentCategoryId === 1 ||
                    category.shipmentCategoryId === 2
                )
                .map((category, index) => {
                  return (
                    <Button
                      key={index}
                      variant="outline-primary"
                      onClick={() => {
                        setActiveShipmentCategory(category.shipmentCategoryId);
                      }}
                      className={
                        activeShipmentCategory === category.shipmentCategoryId
                          ? "active"
                          : ""
                      }
                      disabled={submitting}
                    >
                      {category.shipmentCategory}
                    </Button>
                  );
                })}
          </ButtonGroup>
        </Card.Header>
        <Tab.Content>
          {shipmentCategories.map((category, index) => {
            return (
              <Tab.Pane key={index} eventKey={category.shipmentCategoryId}>
                <Card.Body>
                  {shipmentRacks && (
                    <ServerSideTable
                      key={activeShipmentCategory}
                      columns={racksTableColumns}
                      data={shipmentRacks}
                      fetchData={fetchData}
                      isLoading={isLoading}
                      paginationOptions={paginationOptions}
                      totalCount={totalCount}
                      handleRowClick={handleRowClick}
                      submitting={submitting}
                    />
                  )}
                </Card.Body>
              </Tab.Pane>
            );
          })}
        </Tab.Content>
      </Tab.Container>
    </Card>
  );
};

export default ShipmentRacks;
